import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Dimensions,
  Animated,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { addCoins } from '../utils/AppState';

interface Coin {
  id: number;
  x: number;
  y: number;
  scale: Animated.Value;
}

type GameState = 'idle' | 'playing' | 'finished';

const GAME_DURATION = 30;
const SPAWN_INTERVAL = 550;
const COIN_LIFETIME = 1400;
const { width: SCREEN_W, height: SCREEN_H } = Dimensions.get('window');

const GamesScreen = ({ onBack }: { onBack: () => void }) => {
  const insets = useSafeAreaInsets();
  const [gameState, setGameState] = useState<GameState>('idle');
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(GAME_DURATION);
  const [coins, setCoins] = useState<Coin[]>([]);
  const [bestScore, setBestScore] = useState(0);
  const coinIdRef = useRef(0);
  const timersRef = useRef<NodeJS.Timeout[]>([]);
  const gameAreaRef = useRef<View>(null);
  const coinsAwardedRef = useRef(false);

  const clearAllTimers = useCallback(() => {
    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current = [];
  }, []);

  const spawnCoin = useCallback(() => {
    const padding = 60;
    const maxX = Math.max(SCREEN_W - padding * 2, 100);
    const maxY = Math.max(SCREEN_H - padding * 2 - insets.top - insets.bottom - 180, 100);
    const x = padding + Math.random() * maxX;
    const y = padding + Math.random() * maxY;
    const id = ++coinIdRef.current;
    const scale = new Animated.Value(0);

    const newCoin: Coin = { id, x, y, scale };
    setCoins((prev) => [...prev, newCoin]);

    Animated.spring(scale, {
      toValue: 1,
      friction: 5,
      useNativeDriver: true,
    }).start();

    const removeTimer = setTimeout(() => {
      setCoins((prev) => prev.filter((c) => c.id !== id));
    }, COIN_LIFETIME);
    timersRef.current.push(removeTimer);
  }, [insets.top, insets.bottom]);

  const collectCoin = useCallback((id: number) => {
    setCoins((prev) => {
      const coin = prev.find((c) => c.id === id);
      if (!coin) return prev;

      Animated.timing(coin.scale, {
        toValue: 1.6,
        duration: 150,
        useNativeDriver: true,
      }).start(() => {
        Animated.timing(coin.scale, {
          toValue: 0,
          duration: 100,
          useNativeDriver: true,
        }).start();
      });

      setScore((s) => s + 1);
      return prev.filter((c) => c.id !== id);
    });
  }, []);

  const startGame = useCallback(() => {
    coinsAwardedRef.current = false;
    setScore(0);
    setTimeLeft(GAME_DURATION);
    setCoins([]);
    setGameState('playing');
    clearAllTimers();
  }, [clearAllTimers]);

  useEffect(() => {
    if (gameState !== 'playing') return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setGameState('finished');
          clearAllTimers();
          setCoins([]);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    const spawner = setInterval(() => {
      spawnCoin();
    }, SPAWN_INTERVAL);

    return () => {
      clearInterval(timer);
      clearInterval(spawner);
      clearAllTimers();
    };
  }, [gameState, spawnCoin, clearAllTimers]);

  useEffect(() => {
    if (gameState === 'finished' && score > 0 && !coinsAwardedRef.current) {
      addCoins(score);
      coinsAwardedRef.current = true;
      if (score > bestScore) {
        setBestScore(score);
      }
    }
  }, [gameState, score, bestScore]);

  useEffect(() => {
    return () => clearAllTimers();
  }, [clearAllTimers]);

  const renderIdle = () => (
    <View style={styles.center}>
      <View style={styles.gameIconWrap}>
        <Ionicons name="game-controller" size={56} color="#FF2CA8" />
      </View>
      <Text style={styles.gameTitle}>Coin Rush</Text>
      <Text style={styles.gameSubtitle}>
        Tap as many coins as you can in {GAME_DURATION} seconds!
      </Text>
      {bestScore > 0 && (
        <Text style={styles.bestScore}>Best: {bestScore} coins</Text>
      )}
      <TouchableOpacity style={styles.playButton} activeOpacity={0.8} onPress={startGame}>
        <Text style={styles.playButtonText}>Play Now</Text>
      </TouchableOpacity>
      <TouchableOpacity style={styles.backLink} activeOpacity={0.7} onPress={onBack}>
        <Text style={styles.backLinkText}>← Back to Home</Text>
      </TouchableOpacity>
    </View>
  );

  const renderPlaying = () => (
    <View style={styles.gameArea} ref={gameAreaRef}>
      <View style={styles.hud}>
        <Text style={styles.hudScore}>💰 {score}</Text>
        <View style={styles.timerPill}>
          <Text style={styles.hudTime}>⏱ {timeLeft}s</Text>
        </View>
      </View>
      <Text style={styles.hint}>Tap the coins before they disappear!</Text>
      {coins.map((coin) => (
        <TouchableOpacity
          key={coin.id}
          style={[
            styles.coin,
            { left: coin.x, top: coin.y, transform: [{ scale: coin.scale }] },
          ]}
          activeOpacity={1}
          onPress={() => collectCoin(coin.id)}
        >
          <Ionicons name="logo-bitcoin" size={36} color="#FFCA38" />
        </TouchableOpacity>
      ))}
    </View>
  );

  const renderFinished = () => (
    <View style={styles.center}>
      <View style={styles.resultIconWrap}>
        <Ionicons name="trophy" size={56} color="#FFCA38" />
      </View>
      <Text style={styles.resultTitle}>Time's Up!</Text>
      <Text style={styles.resultScore}>{score} coins collected</Text>
      <Text style={styles.resultSubtitle}>+{score} coins added to your wallet!</Text>
      {score >= bestScore && score > 0 && (
        <Text style={styles.newBest}>New Best Score!</Text>
      )}
      <TouchableOpacity style={styles.playButton} activeOpacity={0.8} onPress={startGame}>
        <Text style={styles.playButtonText}>Play Again</Text>
      </TouchableOpacity>
      <TouchableOpacity style={styles.backLink} activeOpacity={0.7} onPress={onBack}>
        <Text style={styles.backLinkText}>← Back to Home</Text>
      </TouchableOpacity>
    </View>
  );

  return (
    <View style={styles.container}>
      <View style={{ height: insets.top }} />
      {gameState === 'idle' && renderIdle()}
      {gameState === 'playing' && renderPlaying()}
      {gameState === 'finished' && renderFinished()}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#080B1F',
  },
  center: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
    gap: 16,
  },
  gameIconWrap: {
    width: 100,
    height: 100,
    borderRadius: 28,
    backgroundColor: 'rgba(255,44,168,0.15)',
    borderWidth: 1,
    borderColor: 'rgba(255,44,168,0.4)',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  gameTitle: {
    color: '#FF2CA8',
    fontSize: 36,
    fontWeight: '800',
  },
  gameSubtitle: {
    color: '#9DA4C9',
    fontSize: 16,
    textAlign: 'center',
    lineHeight: 22,
    marginTop: 4,
  },
  bestScore: {
    color: '#FFCA38',
    fontSize: 15,
    fontWeight: '700',
    marginTop: 4,
  },
  playButton: {
    marginTop: 20,
    backgroundColor: '#B748FF',
    paddingHorizontal: 48,
    paddingVertical: 16,
    borderRadius: 28,
    shadowColor: '#B748FF',
    shadowOpacity: 0.4,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 4 },
  },
  playButtonText: {
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: '700',
  },
  backLink: {
    marginTop: 8,
    paddingVertical: 8,
  },
  backLinkText: {
    color: '#8D93B3',
    fontSize: 15,
    fontWeight: '500',
  },
  gameArea: {
    flex: 1,
    position: 'relative',
  },
  hud: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingTop: 12,
    paddingBottom: 8,
  },
  hudScore: {
    color: '#FFCA38',
    fontSize: 22,
    fontWeight: '700',
  },
  timerPill: {
    backgroundColor: 'rgba(255,255,255,0.1)',
    borderRadius: 20,
    paddingHorizontal: 16,
    paddingVertical: 6,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.15)',
  },
  hudTime: {
    color: '#F5F7FF',
    fontSize: 18,
    fontWeight: '700',
  },
  hint: {
    color: '#6E728F',
    fontSize: 13,
    textAlign: 'center',
    marginBottom: 8,
  },
  coin: {
    position: 'absolute',
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: 'rgba(255,202,56,0.15)',
    borderWidth: 2,
    borderColor: '#FFCA38',
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#FFCA38',
    shadowOpacity: 0.5,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 0 },
    zIndex: 10,
  },
  resultIconWrap: {
    width: 100,
    height: 100,
    borderRadius: 28,
    backgroundColor: 'rgba(255,202,56,0.15)',
    borderWidth: 1,
    borderColor: 'rgba(255,202,56,0.4)',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  resultTitle: {
    color: '#F5F7FF',
    fontSize: 32,
    fontWeight: '800',
  },
  resultScore: {
    color: '#FFCA38',
    fontSize: 42,
    fontWeight: '800',
    marginTop: 4,
  },
  resultSubtitle: {
    color: '#9DA4C9',
    fontSize: 16,
    marginTop: 4,
  },
  newBest: {
    color: '#FF2CA8',
    fontSize: 16,
    fontWeight: '700',
    marginTop: 4,
  },
});

export default GamesScreen;
